import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'

export type BillingPlan = 'trial' | 'exempt' | 'active' | 'locked'
export type BillingInterval = 'month' | 'year'

export interface BillingStatus {
  subscription_status: 'trial' | 'active' | 'expired' | null
  // Computed by the backend: `exempt` = card-exempt country (India) after its
  // trial, never charged; `locked` = trial over (or subscription ended) without a card.
  plan?: BillingPlan | null
  trial_start_on?: string | null
  trial_end_on?: string | null
  trial_days_remaining?: number | null
  is_locked: boolean
  lock_reason?: 'trial_ended' | 'subscription_ended' | null
  user_count?: number
  /** Admin / Manager licences (anyone who can use the web portal). */
  admin_count?: number
  /** Field Worker licences (mobile app only). */
  worker_count?: number
  has_card?: boolean
  card?: BillingCard | null
  /** Card-exempt country (e.g. India): signs up and grows without a card. */
  card_exempt?: boolean
  /** Only the organization's owner may add or change the card. */
  can_manage_card?: boolean
  /** What the team costs a month and a year; `missing` lists prices not set up yet. */
  price?: BillingPricePreview
  /** SMS, WhatsApp and map use this month: shown, never charged. */
  usage?: BillingUsage
}

/** One billing period's bill. Amounts are strings with 2 decimals. */
export interface BillingQuote {
  currency: string
  interval: BillingInterval
  /** The plan the team's size falls in; the whole team pays its rates. */
  plan_key: string
  plan_title: string
  admins: number
  workers: number
  /** Users the plan fee covers (Field Workers first). */
  included_users: number
  charged_admins: number
  charged_workers: number
  base_amount: string
  admin_amount: string
  worker_amount: string
  minimum_amount: string
  subtotal: string
  topup: string
  total: string
  /** More users than the largest plan: billed at its rates until an Enterprise agreement. */
  over_largest_plan?: boolean
}

export interface BillingPricePreview {
  currency: string
  missing: string[]
  month: BillingQuote | null
  year: BillingQuote | null
}

export interface BillingUsage {
  period_start: string
  sms: number
  phone_codes: number
  whatsapp_messages: number
  map_lookups: number
}

/** The card on file (never the number or CVV: Stripe keeps those) and what Stripe bills now. */
export interface BillingCard {
  brand: string
  last4: string
  exp_month: number | null
  exp_year: number | null
  currency: string
  interval: BillingInterval
  plan_key?: string
  base_amount?: string
  billed_admins: number
  billed_workers: number
  admin_amount: string
  worker_amount: string
  minimum_amount: string
  topup_amount: string
  total_amount: string
  /** Stripe couldn't charge the card; cleared when a payment goes through. */
  payment_failed?: boolean
  payment_failed_at?: string | null
}

export interface CardSetup {
  client_secret: string
  setup_intent_id: string
  publishable_key: string
}

const getObjectPayload = <T>(payload: unknown): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as Record<string, unknown>).data as T
  }
  return payload as T
}

export const billingApi = {
  getStatus: async (): Promise<BillingStatus> => {
    const response = await api.get(API_PATHS.billing.status)
    return getObjectPayload<BillingStatus>(response.data)
  },

  /** Starts adding (or replacing) the org's card; the browser confirms it with Stripe. */
  cardSetup: async (): Promise<CardSetup> => {
    const response = await api.post(API_PATHS.billing.cardSetup, {})
    return getObjectPayload<CardSetup>(response.data)
  },

  /**
   * Saves the confirmed card: starts the subscription (billed monthly or
   * annually), or replaces the card future bills use.
   */
  cardConfirm: async (setupIntentId: string, interval: BillingInterval = 'month'): Promise<BillingStatus> => {
    const response = await api.post(API_PATHS.billing.cardConfirm, { setup_intent_id: setupIntentId, interval })
    return getObjectPayload<BillingStatus>(response.data)
  },

  /** Monthly -> annual, effective now. There's no way back to monthly. */
  switchToAnnual: async (): Promise<BillingStatus> => {
    const response = await api.post(API_PATHS.billing.switchToAnnual, {})
    return getObjectPayload<BillingStatus>(response.data)
  },
}
